import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';

import type { User } from '@recruitee/account-types';
import { I18nService, TranslatePipe } from '@recruitee/i18n';
import { OfferSimpleListItem } from '@recruitee/offers-ui';
import {
  AvatarComponent,
  BoxComponent,
  DividerComponent,
  HeadingComponent,
  IconComponent,
  RadioFormGroupComponent,
  RadioFormGroupItemComponent,
  RadioGroupItem,
  RtTooltip,
  TruncateComponent,
} from '@recruitee/user-interface';

import { AccessOptionsConfig, AccessValue, SHARE_ON_THE_GO_I18N, ShareOnTheGoI18n } from '../types';

@Component({
  selector: 'rt-user-item',
  templateUrl: './user-item.component.html',
  styleUrls: ['./user-item.component.less'],
  standalone: true,
  imports: [
    AvatarComponent,
    BoxComponent,
    DividerComponent,
    HeadingComponent,
    RadioFormGroupComponent,
    TruncateComponent,
    ReactiveFormsModule,
    RadioFormGroupItemComponent,
    IconComponent,
    TranslatePipe,
    RtTooltip,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserItemComponent implements AfterViewInit {
  @Input()
  public user: User;

  @Input()
  public accessOptionsConfig?: AccessOptionsConfig;

  @Input()
  public offer?: OfferSimpleListItem;

  public selectedOption = new FormControl(AccessValue.CandidateOnly);
  public options: Array<RadioGroupItem<AccessValue>> = [];

  @Output()
  public valueChanges = this.selectedOption.valueChanges;

  @ViewChild('radioTpl', { static: true })
  private radioTpl: RadioGroupItem['template'];

  @ViewChild('radioWithInfoIconTpl', { static: true })
  private radioWithInfoIconTpl: RadioGroupItem['template'];

  constructor(
    private i18n: I18nService,
    private cdRef: ChangeDetectorRef,
    @Inject(SHARE_ON_THE_GO_I18N) public shareOnTheGoI18n: ShareOnTheGoI18n,
  ) {}

  public ngAfterViewInit(): void {
    this.setOptions();
    this.setFirstAvailableSelectedOption();

    this.cdRef.detectChanges();
  }

  public isOptionEnabled(optionItem: RadioGroupItem<AccessValue>): boolean {
    return this.accessOptionsConfig![optionItem.value].isEnabled;
  }

  private setOptions(): void {
    if (!this.accessOptionsConfig) {
      return;
    }

    this.options = <Array<RadioGroupItem<AccessValue>>>[
      {
        option: this.i18n.translate(
          'app.candidate.share_on_the_go.allow_access_to_candidate.allow_only_to_this_candidate',
        ),
        value: AccessValue.CandidateOnly,
        template: this.radioTpl,
      },
      {
        option: this.i18n.translate(
          'app.candidate.share_on_the_go.allow_access_to_candidate.allow_to_all_candidates_in_offer',
          { offerTitle: this.offer!.title },
        ),
        value: AccessValue.AllCandidatesInOffer,
        template: this.radioTpl,
      },
      {
        option: this.i18n.translate(this.shareOnTheGoI18n.dontRequestOption, {
          fullUserName: `${this.user.firstName} ${this.user.lastName}`,
        }),
        value: AccessValue.DontRequest,
        template: this.radioWithInfoIconTpl,
      },
    ].filter(({ value }) => this.accessOptionsConfig![value].isVisible);
  }

  private setFirstAvailableSelectedOption(): void {
    if (!this.accessOptionsConfig) {
      this.selectedOption.setValue(null);
      return;
    }

    if (this.accessOptionsConfig[AccessValue.CandidateOnly].isEnabled) {
      this.selectedOption.setValue(AccessValue.CandidateOnly);
    } else if (
      !this.accessOptionsConfig[AccessValue.CandidateOnly].isEnabled &&
      this.accessOptionsConfig![AccessValue.AllCandidatesInOffer].isEnabled
    ) {
      this.selectedOption.setValue(AccessValue.AllCandidatesInOffer);
    } else if (
      !this.accessOptionsConfig![AccessValue.AllCandidatesInOffer].isEnabled &&
      this.accessOptionsConfig[AccessValue.DontRequest].isVisible
    ) {
      this.selectedOption.setValue(AccessValue.DontRequest);
    } else {
      this.selectedOption.setValue(null);
    }
  }
}
